/**
* Nom : companyNew.js
* Description : Ce fichier contient la définition de la page companyNew 
*               Cette page fourni un formulaire pour créer une société
* Date de création : 7 juillet 2025
*/

/* CSS et assets */
// None

/* Bibliothèques et composants */
import { TabC } from 'package_ui_ihm/dist'
import { Container } from 'package_ui_ihm/dist'
import { BackPage } from 'package_ui_ihm/dist'
import { InputForm } from 'package_ui_ihm/dist'

/* Hook */
import { useState, useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import { useAuth } from 'package_ui_ihm/dist'
import { useApi } from 'package_ui_ihm/dist'
import { useNavigate } from 'react-router-dom'
import { useScreenWidth } from 'package_ui_ihm/dist'


/**
* CompanyNew : affiche une page avec un formulaire pour créer une société
*              un lien permet de revenir sur la page qui liste toutes les sociétés 
*
* Type : page 
*
* Parametres : 
*  None
*/
function CompanyNew() {

    // Define useState hooks
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [code, setCode] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [statusCompany, setStatusCompany] = useState(null)
    const [nameError, setNameError] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const [codeError, setCodeError] = useState(false);
    const [cityError, setCityError] = useState(false);
    const [countryError, setCountryError] = useState(false);
    const [skipPrompt, setSkipPrompt] = useState(false);


    // Define useContext hooks
    const { authToken } = useAuth();
    const { path } = useApi();

    const navigate = useNavigate();


    // Define useBlocker hooks
    const shouldBlock = 
        ((name !== "") || 
        (address !== "") || 
        (code !== "") ||
        (city !== "") ||
        (country !== "")) && 
        (skipPrompt === false);
    const blocker = useBlocker(shouldBlock);


    // Define useScreenWidth hooks
    const screenWidth = useScreenWidth();


    // Define functions
    async function sendData(name, address, code, city, country) {
        // Token
        const token = "Bearer " + authToken
        // Création du Form Data
        let form_data = new FormData();
        form_data.append("name", name);
        form_data.append("address", address);
        form_data.append("code", code);
        form_data.append("city", city);
        form_data.append("country", country);
        // Create company
        const response = await fetch(path + "/api/au-cony/", 
            {
                method: 'POST',
                credentials: 'include',
                mode: 'cors',
                headers: {
                    accept: 'application/json',
                    //'Content-Type': 'multipart/form-data',  cette ligne ne doit pas être utilisée avec FormData sinon erreur
                    Authorization: token,
                },
                body: form_data
            }
        )
        setStatusCompany(response["status"])
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        setSkipPrompt(true);
        if(name === null || name === ''){
            setNameError(true)
        }
        else
        {   
            setNameError(false)
        }
        if(address === null || address === ''){
            setAddressError(true)
        }
        else
        {   
            setAddressError(false)
        }
        if(code === null || code === ''){
            setCodeError(true)
        }
        else
        {   
            setCodeError(false)
        }
        if(city === null || city === ''){
            setCityError(true)
        }
        else
        {   
            setCityError(false)
        }
        if(country === null || country === ''){
            setCountryError(true)
        }
        else
        {   
            setCountryError(false)
        }
        if(nameError === false && addressError === false && codeError === false && cityError === false && countryError === false){
            sendData(name, address, code, city, country)
        }
    };


    // Define useEffect hooks

    useEffect(() => {
        if(statusCompany === 201) {
            navigate('/companies/')
        } else {
            setSkipPrompt(false);
            navigate('/companies/new')
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusCompany, navigate]);

    useEffect(() => {
        // Bloquer la fermeture d'onglet/rafraîchissement
        const handleBeforeUnload = (e) => {
            if (shouldBlock === true) {
                // Standard pour afficher une confirmation (les navigateurs modernes gèrent le message)
                e.preventDefault();
                e.returnValue = "";
                // Certains navigateurs nécessitent cette ligne pour afficher la confirmation
                return (e.returnValue = "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées.");
            }
        }; 

        window.addEventListener("beforeunload", handleBeforeUnload);

        // Nettoyer l'écouteur quand le composant est démonté
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldBlock]);

    useEffect(() => {
        if (blocker.state === "blocked") {
            const confirmed = window.confirm(
                "Cette page vous demande de confirmer sa fermeture ; des données que vous avez saisies pourraient ne pas être enregistrées."
            );

            if (confirmed) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker]);


    // Render component
    return (
        <div>
            <TabC tabNumber={5}/>
            <div className={(screenWidth > 650) ? 'App' : 'App-ss'}>
                <Container>
                    <h1>Créer une nouvelle société</h1>
                    {/*Lien de retour*/}
                    <div className='flex-row justify-start margin-left margin-bottom margin-top'>
                        <div className='link-border'>
                            <BackPage path='/companies'>Retour à la liste des sociétés</BackPage>
                        </div>
                    </div>
                    {(statusCompany !== null && statusCompany !== 201 && nameError === false && addressError === false && codeError === false && cityError === false && countryError === false) ? (
                        <p className='form-error'>La société existe déjà ou n'a pas pu être enregistrée !</p>
                    ) : (
                        <p/>
                    )}  
                    <form onSubmit={handleSubmit} className='flex-column'>
                        <div className='flex-column form-center'>
                            <InputForm error={nameError} type="text" label="Nom" value={name} setValue={setName} disabled={false}/>
                            <InputForm error={addressError} type="text" label="Numéro et nom de rue" value={address} setValue={setAddress} disabled={false}/>
                            <InputForm error={codeError} type="text" label="Code postal" value={code} setValue={setCode} disabled={false}/>
                            <InputForm error={cityError} type="text" label="Ville" value={city} setValue={setCity} disabled={false}/>
                            <InputForm error={countryError} type="text" label="Pays" value={country} setValue={setCountry} disabled={false}/>
                            <div></div>               
                        </div>
                        <div className='flex-row justify-end'>
                            <button type="submit" className='button'>Valider</button>
                        </div>
                    </form> 
                </Container>
            </div>
        </div>
    );
}

export default CompanyNew;
